/**
 * Google Ads Conversion Tracking Utility
 * Fires conversion events with Enhanced Conversions (user_data)
 * for better match rates in Google Ads.
 */

// Google Ads Conversion ID and Label
const CONVERSION_ID = 'AW-17797421129';
const CONVERSION_LABEL = 'LrUYCIi_tdMbEMmwvKZC';

/**
 * Split a full name into first and last name parts.
 * @param {string} fullName
 * @returns {{ firstName: string, lastName: string }}
 */
const splitName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || ''
  };
};

/**
 * Normalize a phone number to E.164-ish format for Google matching.
 * Strips non-digits, prepends +1 if 10 digits (US).
 * @param {string} phone
 * @returns {string}
 */
const normalizePhone = (phone = '') => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
};

/**
 * Build the user_data object Google expects for Enhanced Conversions.
 * All fields are optional — Google hashes them automatically.
 *
 * @param {object} opts
 * @param {string} [opts.email]
 * @param {string} [opts.phone]
 * @param {string} [opts.firstName]
 * @param {string} [opts.lastName]
 * @param {string} [opts.fullName]     - Split into first/last if firstName isn't provided
 * @param {string} [opts.street]
 * @param {string} [opts.city]
 * @param {string} [opts.region]       - State / province
 * @param {string} [opts.postalCode]
 * @param {string} [opts.country]      - ISO 3166-1 alpha-2 (defaults to US)
 * @returns {object|null}
 */
const buildUserData = (opts = {}) => {
  const data = {};

  // Email
  if (opts.email) {
    data.email = opts.email.trim().toLowerCase();
  }

  // Phone
  const phone = normalizePhone(opts.phone);
  if (phone) {
    data.phone_number = phone;
  }

  // Name — prefer explicit first/last, fall back to splitting fullName
  let firstName = opts.firstName || '';
  let lastName = opts.lastName || '';
  if (!firstName && opts.fullName) {
    const split = splitName(opts.fullName);
    firstName = split.firstName;
    lastName = split.lastName;
  }

  // Address block
  const address = {};
  if (firstName) address.first_name = firstName.trim().toLowerCase();
  if (lastName) address.last_name = lastName.trim().toLowerCase();
  if (opts.street) address.street = opts.street.trim().toLowerCase();
  if (opts.city) address.city = opts.city.trim().toLowerCase();
  if (opts.region) address.region = opts.region.trim().toUpperCase();
  if (opts.postalCode) address.postal_code = opts.postalCode.trim();
  address.country = (opts.country || 'US').trim().toUpperCase();

  if (Object.keys(address).length > 1) {
    // Only attach if we have more than just the default country
    data.address = address;
  }

  return Object.keys(data).length > 0 ? data : null;
};

/**
 * Track a form submission conversion for Google Ads with Enhanced Conversions.
 *
 * @param {string}  formType         - Label for logging (e.g. 'lead_form')
 * @param {object}  [userData={}]    - User fields for Enhanced Conversions
 * @param {object}  [conversionOpts] - Extra gtag conversion params (value, currency, etc.)
 * @returns {boolean}
 */
export const trackFormConversion = (formType = 'lead_form', userData = {}, conversionOpts = {}) => {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      console.warn('⚠️ gtag not available — conversion not tracked');
      return false;
    }

    // 1) Set Enhanced Conversion user_data BEFORE firing the conversion event
    const userDataPayload = buildUserData(userData);
    if (userDataPayload) {
      window.gtag('set', 'user_data', userDataPayload);
      console.log(`📧 Enhanced Conversion user_data set for: ${formType}`, userDataPayload);
    }

    // 2) Fire the Google Ads conversion event
    window.gtag('event', 'conversion', {
      'send_to': `${CONVERSION_ID}/${CONVERSION_LABEL}`,
      'value': 50.0,
      'currency': 'USD',
      ...conversionOpts
    });

    console.log(`✅ Google Ads conversion tracked: ${formType}`);

    // 3) Fire Meta Pixel Lead event (if pixel is loaded)
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: formType,
        content_category: 'form_submission'
      });
      console.log(`✅ Meta Pixel Lead event fired: ${formType}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error tracking conversion:', error);
    return false;
  }
};

/**
 * Convenience wrappers — each accepts a user data object.
 */
export const trackLeadFormConversion = (userData = {}) => {
  return trackFormConversion('lead_form', userData);
};

export const trackFinanceApplicationConversion = (userData = {}) => {
  return trackFormConversion('finance_application', userData);
};

export const trackContactFormConversion = (userData = {}) => {
  return trackFormConversion('contact_form', userData);
};

export const trackAppointmentConversion = (userData = {}) => {
  return trackFormConversion('appointment', userData);
};

export const trackTestDriveConversion = (userData = {}) => {
  return trackFormConversion('test_drive', userData);
};

export const trackTradeInConversion = (userData = {}) => {
  return trackFormConversion('trade_in_evaluation', userData);
};

export const trackPurchaseConversion = (userData = {}, transactionId = '', amount = 0) => {
  return trackFormConversion('purchase', userData, {
    'value': parseFloat(amount) || 0,
    'currency': 'USD',
    'transaction_id': transactionId
  });
};

export default {
  trackFormConversion,
  trackLeadFormConversion,
  trackFinanceApplicationConversion,
  trackContactFormConversion,
  trackAppointmentConversion,
  trackTestDriveConversion,
  trackTradeInConversion,
  trackPurchaseConversion
};
